import { X } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { getRuntimeApiBase } from "../../lib/runtime-api-base";
import type {
  EconHeaderCountdownEvent,
  EconHeaderIntelligenceRecord,
} from "../../types/econ-header";
import { FadingRuler } from "../shared/FadingRuler";
import { ReleasedDeskPlanEvent } from "./ReleasedDeskPlanEvent";

export const LATEST_ECON_PRINT_DRAWER_ID = "latest-econ-print-drawer";

interface LatestEconPrintResponse {
  availability: "available" | "unavailable" | "stale";
  print: EconHeaderIntelligenceRecord | null;
  reason: string;
  latestObservedAt: string | null;
}

interface LatestEconPrintDrawerProps {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  latestReleasedEvent?: EconHeaderCountdownEvent | null;
}

interface DrawerPosition {
  top: number;
  left: number;
}

export function LatestEconPrintDrawer({
  anchorRef,
  open,
  onClose,
  latestReleasedEvent,
}: LatestEconPrintDrawerProps) {
  const [position, setPosition] = useState<DrawerPosition>({ top: 8, left: 8 });
  const [state, setState] = useState<
    | { status: "idle" | "loading" }
    | { status: "error"; message: string }
    | { status: "ready"; response: LatestEconPrintResponse }
  >({ status: "idle" });
  const closeAndRestoreFocus = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => {
      anchorRef.current?.focus({ preventScroll: true });
    });
  }, [anchorRef, onClose]);

  useLayoutEffect(() => {
    if (!open) return;
    const reposition = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(300, window.innerWidth - 16);
      setPosition({
        top: Math.min(rect.bottom - 1, window.innerHeight - 8),
        left: Math.max(
          8,
          Math.min(rect.right - width, window.innerWidth - width - 8),
        ),
      });
    };
    reposition();
    window.addEventListener("resize", reposition);
    return () => window.removeEventListener("resize", reposition);
  }, [anchorRef, open]);

  useEffect(() => {
    if (!open) return;
    if (latestReleasedEvent !== undefined) return;
    const controller = new AbortController();
    setState({ status: "loading" });
    void fetch(
      `${getRuntimeApiBase()}/api/econ/header-intelligence/recent?view=latest-print`,
      { signal: controller.signal },
    )
      .then(async (response) => {
        if (!response.ok)
          throw new Error(`Request failed (${response.status})`);
        return (await response.json()) as LatestEconPrintResponse;
      })
      .then((response) => setState({ status: "ready", response }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "error",
          message:
            error instanceof Error ? error.message : "Latest print unavailable",
        });
      });
    return () => controller.abort();
  }, [latestReleasedEvent, open]);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeAndRestoreFocus();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [closeAndRestoreFocus, open]);

  if (typeof document === "undefined" || !open) return null;

  return createPortal(
    <section
      id={LATEST_ECON_PRINT_DRAWER_ID}
      role="dialog"
      aria-label="Latest economic print"
      aria-modal="false"
      data-latest-econ-print-state={
        latestReleasedEvent !== undefined ? "provided" : state.status
      }
      className="fintheon-popover-surface pointer-events-auto fixed z-[9999] w-[min(300px,calc(100vw-16px))] translate-y-0 rounded-t-none p-3 text-[var(--fintheon-text)] opacity-100"
      style={position}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[9px] uppercase tracking-[0.14em] text-[var(--fintheon-text)]/42">
            Stored RiskFlow print
          </p>
          <h3 className="mt-0.5 text-[12px] font-semibold text-[var(--fintheon-text)]">
            Latest econ print
          </h3>
        </div>
        <button
          type="button"
          className="rounded-[4px] p-1 text-[var(--fintheon-text)]/48 transition-colors hover:text-[var(--fintheon-text)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--fintheon-accent)]/55"
          aria-label="Close latest economic print"
          onClick={closeAndRestoreFocus}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <FadingRuler className="mt-2 opacity-45" />
      <div className="mt-2 min-h-[78px]">
        {latestReleasedEvent !== undefined ? (
          <ReleasedDeskPlanEvent event={latestReleasedEvent} />
        ) : null}
        {latestReleasedEvent === undefined &&
        (state.status === "idle" || state.status === "loading") ? (
          <p className="text-[11px] text-[var(--fintheon-text)]/52">
            Loading the latest stored print…
          </p>
        ) : null}
        {latestReleasedEvent === undefined && state.status === "error" ? (
          <div data-latest-econ-print-availability="error">
            <p className="text-[11px] text-[var(--fintheon-bearish)]">
              Latest print unavailable
            </p>
            <p className="mt-1 text-[10px] text-[var(--fintheon-text)]/46">
              {state.message}
            </p>
          </div>
        ) : null}
        {latestReleasedEvent === undefined && state.status === "ready" ? (
          <PrintContent response={state.response} />
        ) : null}
      </div>
    </section>,
    document.body,
  );
}

function PrintContent({ response }: { response: LatestEconPrintResponse }) {
  if (response.availability !== "available" || !response.print) {
    return (
      <div data-latest-econ-print-availability={response.availability}>
        <p className="text-[11px] font-medium text-[var(--fintheon-text)]/72">
          {response.availability === "stale"
            ? "No current print"
            : "No stored print available"}
        </p>
        <p className="mt-1 text-[10px] leading-4 text-[var(--fintheon-text)]/46">
          {response.reason}
        </p>
        {response.latestObservedAt ? (
          <p className="mt-2 font-mono text-[9px] text-[var(--fintheon-text)]/36">
            Last observed {formatTimestamp(response.latestObservedAt)}
          </p>
        ) : null}
      </div>
    );
  }

  const print = response.print;
  return (
    <div data-latest-econ-print-availability="available">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-[12px] font-medium text-[var(--fintheon-text)]">
            {print.eventName}
          </p>
          <p className="mt-0.5 truncate text-[9px] text-[var(--fintheon-text)]/40">
            {formatSource(print.actualSource)}
          </p>
        </div>
        <span className="shrink-0 font-mono text-[9px] text-[var(--fintheon-text)]/42">
          {formatTimestamp(print.actualPrintedAt ?? print.updatedAt)}
        </span>
      </div>
      <div className="mt-3 flex items-stretch gap-2">
        <PrintValue label="Previous" value={print.previousValue} />
        <FadingRuler
          orientation="vertical"
          className="self-stretch opacity-35"
        />
        <PrintValue label="Forecast" value={print.calendarForecast} />
        <FadingRuler
          orientation="vertical"
          className="self-stretch opacity-35"
        />
        <PrintValue label="Actual" value={print.actualValue} accent />
      </div>
      <p className="mt-3 text-[10px] leading-4 text-[var(--fintheon-text)]/54">
        {print.scoreImpact.direction
          ? `${capitalize(print.scoreImpact.direction)} versus forecast. `
          : ""}
        {print.scoreImpact.rationale}
      </p>
    </div>
  );
}

function PrintValue(input: {
  label: string;
  value: string | null;
  accent?: boolean;
}) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-[8px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/34">
        {input.label}
      </p>
      <p
        className={`mt-0.5 font-mono text-[12px] font-semibold ${
          input.accent
            ? "text-[var(--fintheon-accent)]"
            : "text-[var(--fintheon-text)]/72"
        }`}
      >
        {input.value ?? "—"}
      </p>
    </div>
  );
}

function formatTimestamp(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "time unknown";
  return parsed.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatSource(value: string): string {
  return value
    .replace(/^riskflow-/, "RiskFlow · ")
    .replace(/-headline$/, "")
    .replaceAll("-", " ");
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
